/**
 * Kruhový buffer nad `Float32Array` pro historii grafů.
 *
 * PROČ ne pole v Zustandu: `[...prev, v].slice(-N)` per sérii a tik (jak to dělá store/bms.ts)
 * by při ~30 sériích znamenalo 30 alokací polí po 180 prvcích ZA SEKUNDU. `push` sem je
 * O(1) zápis do předalokované paměti — nula alokací.
 *
 * `NaN` má zvláštní význam: DÍRA V DATECH (pauza, reconnect, výpadek hostitele).
 * `buildPath` na ní ukončí subpath, takže graf ukáže skutečnou mezeru místo lživé přímky.
 */

export interface Ring {
  buf: Float32Array;
  /** Index, kam se zapíše další vzorek. */
  head: number;
  /** Kolik vzorků je platných (roste do `cap`). */
  count: number;
  cap: number;
}

export const createRing = (cap: number): Ring => ({
  buf: new Float32Array(Math.max(1, cap)),
  head: 0,
  count: 0,
  cap: Math.max(1, cap),
});

export const push = (r: Ring, value: number): void => {
  r.buf[r.head] = value;
  r.head = (r.head + 1) % r.cap;
  if (r.count < r.cap) r.count += 1;
};

/**
 * Vysype vzorky chronologicky do předaného pole (bez alokace). Vrací počet zapsaných.
 * Když je `out` kratší než obsah, vezme se POSLEDNÍCH `out.length` vzorků.
 */
export const toArray = (r: Ring, out: Float32Array): number => {
  const n = Math.min(r.count, out.length);
  const start = (r.head - n + r.cap * 2) % r.cap;
  for (let i = 0; i < n; i++) out[i] = r.buf[(start + i) % r.cap];
  return n;
};

export interface RingStats {
  min: number;
  max: number;
  last: number;
  hasData: boolean;
}

/**
 * Minimum, maximum a poslední hodnota JEDNÍM průchodem. Spread do `Math.min(...arr)`
 * je pravidly zakázaný (stack overflow nad ~10k prvky) a stejně by to byly tři průchody.
 * `NaN` (díra) se do min/max nepočítá.
 */
export const stats = (r: Ring): RingStats => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let last = Number.NaN;
  let hasData = false;

  const n = r.count;
  const start = (r.head - n + r.cap * 2) % r.cap;
  for (let i = 0; i < n; i++) {
    const v = r.buf[(start + i) % r.cap];
    if (Number.isNaN(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
    last = v;
    hasData = true;
  }

  return hasData ? { min, max, last, hasData } : { min: 0, max: 0, last: Number.NaN, hasData: false };
};

export const clearRing = (r: Ring): void => {
  r.head = 0;
  r.count = 0;
};
